import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { Link } from 'react-router-dom'
import { Star, CheckCircle, ArrowLeft, Loader, ExternalLink } from 'lucide-react'

function StarRating({ value, onChange, size = 24 }) {
  const [hover, setHover] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110"
        >
          <Star
            size={size}
            className={`transition-colors ${
              star <= (hover || value)
                ? 'text-ochre fill-ochre'
                : 'text-border-darker'
            }`}
          />
        </button>
      ))}
    </div>
  )
}

function PageHeader({ title, subtitle }) {
  return (
    <section className="bg-charcoal relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-6 h-0.5 bg-terracotta" />
          <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">Feedback</span>
        </div>
        <h1 className="font-display text-cream tracking-[0.04em] leading-[0.95]" style={{ fontSize: 'clamp(48px, 6vw, 88px)' }}>
          {title}
        </h1>
        {subtitle && <p className="text-sm text-muted mt-4 max-w-xl">{subtitle}</p>}
      </div>
    </section>
  )
}

export default function ReviewPage() {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')
  const [reviews, setReviews] = useState([])

  useEffect(() => {
    fetch('/api/reviews')
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setReviews(data.filter(r => r.status === 'approved')) })
      .catch(err => {
        Sentry.captureException(err, { extra: { fn: 'Review.fetchReviews' } })
      })
  }, [])

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null

  const handleSubmit = async () => {
    if (!rating) return
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: 'overall',
          product_name: 'Overall Experience',
          business: 'Desres Kitchen & Confectionery',
          category: 'Overall',
          rating,
          comment: comment.trim(),
          reviewer_name: name.trim() || 'Anonymous',
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to submit review')
      setSubmitted(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const resetForm = () => {
    setRating(0)
    setComment('')
    setName('')
    setSubmitted(false)
    setError('')
  }

  // ─── Success screen ───
  if (submitted) {
    return (
      <div>
        <PageHeader title="Thank You!" />
        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        <section className="bg-umber">
          <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-28 text-center">
            <CheckCircle size={56} className="text-terracotta mx-auto mb-6" />
            <h2 className="font-display text-cream text-3xl tracking-wide mb-4">Review Submitted</h2>
            <p className="text-sm text-ink-mid mb-2">You gave us {rating} out of 5 stars.</p>
            <p className="text-sm text-muted mb-8">Your feedback helps us improve. Thank you!</p>
            <p className="text-sm text-ink-mid mb-6">Enjoyed your experience? Help others find us by leaving a Google Review too!</p>
            <a
              href="https://search.google.com/local/writereview?placeid=PLACEHOLDER"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white text-cream border border-border-dark rounded-full font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 hover:border-ochre hover:text-ochre hover:-translate-y-0.5 transition-all mb-6"
            >
              <ExternalLink size={14} /> Review Us on Google
            </a>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={resetForm} className="inline-flex items-center justify-center gap-2 bg-terracotta text-white rounded-full font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 hover:bg-terracotta-glow hover:-translate-y-0.5 transition-all">
                Leave Another Review
              </button>
              <Link to="/" className="inline-flex items-center justify-center gap-2 bg-transparent text-cream border border-border-dark rounded-full font-body text-[13px] font-bold tracking-[0.1em] uppercase px-9 py-4 hover:border-terracotta hover:text-terracotta transition-all">
                Place an Order
              </Link>
            </div>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="Reviews" subtitle="We'd love to hear about your experience with us." />
      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
      <section className="bg-umber">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
          <Link to="/" className="text-sm text-terracotta hover:underline mb-8 inline-flex items-center gap-1 font-body tracking-[0.05em] uppercase">
            <ArrowLeft size={14} /> Back to Ordering
          </Link>

          {/* Overall rating summary */}
          {avgRating && (
            <div className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden mb-8">
              <div className="p-6 flex items-center gap-4">
                <div className="font-display text-ochre" style={{ fontSize: '48px', lineHeight: 1 }}>{avgRating}</div>
                <div>
                  <div className="flex gap-0.5 mb-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={16} className={s <= Math.round(Number(avgRating)) ? 'text-ochre fill-ochre' : 'text-border-darker'} />
                    ))}
                  </div>
                  <p className="text-xs text-muted">Based on {reviews.length} review{reviews.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          )}

          {/* Review form */}
          <div className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden">
            <div className="p-8 space-y-6">
              <div className="text-center">
                <h3 className="font-display text-cream text-2xl tracking-wide">How Was Your Experience?</h3>
                <p className="text-xs text-muted mt-2">Rate us and let us know how we're doing.</p>
              </div>

              <div className="text-center">
                <div className="flex justify-center"><StarRating value={rating} onChange={setRating} size={40} /></div>
                {rating > 0 && (
                  <p className="text-sm text-ochre mt-3 font-body font-semibold">
                    {rating === 1 ? 'Poor' : rating === 2 ? 'Fair' : rating === 3 ? 'Good' : rating === 4 ? 'Very Good' : 'Excellent'}
                  </p>
                )}
              </div>

              <div>
                <label className="text-xs text-muted mb-1 block font-body">Tell us more (optional)</label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="What did you enjoy? Anything we can improve?"
                  rows={3}
                  className="w-full bg-surface-high border border-border-dark rounded-xl px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta resize-none"
                />
              </div>

              <div>
                <label className="text-xs text-muted mb-1 block font-body">Your Name (optional)</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Anonymous"
                  className="w-full bg-surface-high border border-border-dark rounded-xl px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta"
                />
              </div>

              {error && <p className="text-sm text-terracotta bg-ember-bg border border-terracotta/30 px-4 py-3">{error}</p>}

              <button
                onClick={handleSubmit}
                disabled={!rating || submitting}
                className="w-full bg-terracotta hover:bg-terracotta-glow disabled:opacity-50 text-white py-4 rounded-full font-body text-[13px] font-bold tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all"
              >
                {submitting ? <><Loader size={16} className="animate-spin" /> Submitting...</> : <><Star size={16} /> Submit Review</>}
              </button>
            </div>
          </div>

          {/* Approved reviews */}
          {reviews.length > 0 && (
            <div className="mt-12">
              <h2 className="font-display text-cream text-2xl tracking-wide mb-6">What Our Customers Say</h2>
              <div className="space-y-4">
                {reviews.slice(0, 10).map(r => (
                  <div key={r.id} className="relative bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden">
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-body font-semibold text-cream">{r.reviewer_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={12} className={s <= r.rating ? 'text-ochre fill-ochre' : 'text-border-darker'} />
                          ))}
                        </div>
                      </div>
                      {r.comment && <p className="text-sm text-ink-mid">{r.comment}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
